// The assistant's Memory (rgip-copilot ADR 0027), reached through Studio's same-origin
// proxy at /api/copilot/v1/memory like every other rgip call.
//
// A decision counts only once a person approved it. The agent (during chat) and a
// reviewer (after an idle session) can only PROPOSE; a proposal is approved, edited
// and approved, or dismissed here. A person writing one down themselves is approved on
// the spot. Every change needs `can_edit` (Studio's ARCHITECT bar, checked by rgip as
// the caller); reads are open to the tenant.
//
// Approved MODEL entries are mirrored by rgip into the model's notes as an
// `rgip:decisions` block (`notesBlocks.readDecisions`). A change that reports
// `notes_synced: false` was kept, but the block was not rewritten this time.

import { whenReadable } from './sessionTime';
import { errorMessage } from './rgipChatApi';

const BASE = '/api/copilot/v1/memory';

/**
 * What the form offers: this model, or every model. rgip stores an entry under a
 * hierarchical key (`studio:models:75`, `studio:models`, then `studio` and `tenant`
 * above); this is the UI's choice, turned into a key only on the wire (`asNewMemory`).
 */
export type MemoryScope = 'tenant' | 'model';

/** Every Studio model: what `tenant` (all models) is stored under. */
export const ALL_MODELS_KEY = 'studio:models';

export function modelKey(modelId: string): string {
  return `${ALL_MODELS_KEY}:${modelId}`;
}

/**
 * Which of the two an entry is. Anything without a model is shared: every model, or a
 * level above (all of Studio, the org), which this UI shows together.
 */
export function scopeOf(entry: Pick<MemoryEntry, 'model_id'>): MemoryScope {
  return entry.model_id ? 'model' : 'tenant';
}
export type MemoryKind =
  | 'override'
  | 'requirement'
  | 'preference'
  | 'mapping_reason'
  | 'learning';

export const MEMORY_KINDS: { value: MemoryKind; label: string }[] = [
  { value: 'requirement', label: 'Requirement' },
  { value: 'preference', label: 'Preference' },
  { value: 'override', label: 'Override' },
  { value: 'mapping_reason', label: 'Mapping reason' },
  { value: 'learning', label: 'Learning' },
];

/** rgip `app/memory/schemas.py: MemoryEntry`, the fields this UI reads. */
export interface MemoryEntry {
  entry_id: string;
  /** Where it applies, e.g. `studio:models:75` or `studio:models`. */
  key: string;
  /** The model a model key names, derived by rgip; null for a shared key. */
  model_id: string | null;
  status: string;
  kind: string;
  text: string;
  instead_of: string | null;
  reason: string | null;
  /** The user's own words the proposal came from, verbatim. */
  quote: string | null;
  origin: 'reviewer' | 'chat' | 'manual' | string;
  thread_id: string | null;
  proposed_by_email: string | null;
  decided_by_email: string | null;
  decided_at: string | null;
  created_at: string;
  /**
   * `supersede` when approving this entry replaces the one `supersedes_id` names: on the
   * same model it retires it, on a model this one was copied from it hides it for this
   * copy only.
   */
  action: 'add' | 'supersede' | string;
  supersedes_id: string | null;
  /** Set on an approved entry that belongs to the model this one was copied from. */
  inherited_from_model_id: string | null;
  notes_synced?: boolean | null;
}

export interface MemoryList {
  entries: MemoryEntry[];
  can_edit: boolean;
}

/**
 * What a person may change on an entry: `EditMemoryRequest`. Only the fields sent change;
 * `null` clears `instead_of` or `reason`. `text` and `kind` cannot be cleared.
 */
export interface MemoryEdits {
  kind?: MemoryKind;
  text?: string;
  instead_of?: string | null;
  reason?: string | null;
}

/** `CreateMemoryRequest`. */
export interface NewMemory {
  key: string;
  kind: MemoryKind;
  text: string;
  instead_of?: string;
  reason?: string;
  /**
   * A model key only: an APPROVED entry from this model's `duplicate_of` lineage that
   * this one replaces for this copy ("Override for this model").
   */
  supersedes_id?: string;
}

/** A refusal from rgip, with its HTTP status so a caller can tell a stale list apart. */
export class MemoryError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'MemoryError';
  }
}

async function refusal(r: Response): Promise<MemoryError> {
  return new MemoryError(await errorMessage(r), r.status);
}

/**
 * 404 on a change means the entry is no longer what this list showed: someone else
 * approved, dismissed, edited or deleted it first.
 */
export const STALE_NOTICE =
  'Someone else already changed that one. The list is up to date now.';

export function isStale(error: unknown): boolean {
  return error instanceof MemoryError && error.status === 404;
}

const EMPTY: MemoryList = { entries: [], can_edit: false };

const tenantQuery = (tenant: string) => `tenant=${encodeURIComponent(tenant)}`;

const json = (method: string, body: unknown): RequestInit => ({
  method,
  credentials: 'same-origin',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/**
 * Approved and proposed entries. With a model: the tenant's, this model's, and those it
 * inherits from the model it was copied from. Without: the tenant's and every model's.
 *
 * A 404 is rgip's switch being off, and reads as "nothing to show" rather than an error.
 */
export async function listMemory(
  tenant: string,
  modelId?: string | null
): Promise<MemoryList> {
  const model = modelId ? `&model_id=${encodeURIComponent(modelId)}` : '';
  const r = await fetch(
    `${BASE}?${tenantQuery(tenant)}${model}&status=approved,proposed`,
    { credentials: 'same-origin' }
  );
  if (r.status === 404) return EMPTY;
  if (!r.ok) throw await refusal(r);
  const doc = (await r.json()) as Partial<MemoryList>;
  return { entries: doc.entries ?? [], can_edit: !!doc.can_edit };
}

/** A decision a person wrote down themselves: approved on the spot. */
export async function createMemory(
  tenant: string,
  entry: NewMemory
): Promise<MemoryEntry> {
  const r = await fetch(`${BASE}?${tenantQuery(tenant)}`, json('POST', entry));
  if (!r.ok) throw await refusal(r);
  return (await r.json()) as MemoryEntry;
}

/** Approve a proposal, with whatever the person changed in it first. */
export async function approveMemory(
  tenant: string,
  entryId: string,
  edits: MemoryEdits = {}
): Promise<MemoryEntry> {
  const r = await fetch(
    `${BASE}/${encodeURIComponent(entryId)}/approve?${tenantQuery(tenant)}`,
    json('POST', edits)
  );
  if (!r.ok) throw await refusal(r);
  return (await r.json()) as MemoryEntry;
}

export async function dismissMemory(
  tenant: string,
  entryId: string
): Promise<void> {
  const r = await fetch(
    `${BASE}/${encodeURIComponent(entryId)}/dismiss?${tenantQuery(tenant)}`,
    { method: 'POST', credentials: 'same-origin' }
  );
  if (!r.ok) throw await refusal(r);
}

export async function editMemory(
  tenant: string,
  entryId: string,
  edits: MemoryEdits
): Promise<MemoryEntry> {
  const r = await fetch(
    `${BASE}/${encodeURIComponent(entryId)}?${tenantQuery(tenant)}`,
    json('PATCH', edits)
  );
  if (!r.ok) throw await refusal(r);
  return (await r.json()) as MemoryEntry;
}

export async function deleteMemory(
  tenant: string,
  entryId: string
): Promise<void> {
  const r = await fetch(
    `${BASE}/${encodeURIComponent(entryId)}?${tenantQuery(tenant)}`,
    { method: 'DELETE', credentials: 'same-origin' }
  );
  if (!r.ok) throw await refusal(r);
}

/**
 * The proposals the chat banner offers: none at all unless this person can decide them,
 * because a banner of decisions you cannot make is a banner you cannot dismiss.
 *
 * `scope` narrows it to one scope. Ask Data Studio passes `tenant`: its list is the whole
 * org's, and the agent there (studio-home) only proposes tenant-wide decisions, so a
 * model's proposal belongs on that model's chat, not on the models page.
 */
export function bannerProposals(
  list: MemoryList | null,
  scope?: MemoryScope
): MemoryEntry[] {
  if (!list || !list.can_edit) return [];
  return list.entries.filter(
    (entry) =>
      entry.status === 'proposed' &&
      (scope === undefined || scopeOf(entry) === scope)
  );
}

/**
 * What approving `entry` replaces, in words, or null when it replaces nothing.
 *
 * The replaced entry is looked up in the list on screen. It may not be there (retired
 * since, or on a model the list does not cover), and then all that can be said is that
 * it replaces an earlier decision.
 */
export function replacedText(
  entry: Pick<MemoryEntry, 'action' | 'supersedes_id'>,
  entries: MemoryEntry[]
): string | null {
  if (entry.action !== 'supersede' || !entry.supersedes_id) return null;
  const old = entries.find((e) => e.entry_id === entry.supersedes_id);
  return old ? `Replaces: ${old.text}` : 'Replaces an earlier decision.';
}

/** The Memory form's fields, as typed. */
export interface MemoryFormValues {
  scope: MemoryScope;
  kind: MemoryKind;
  text: string;
  instead_of: string;
  reason: string;
}

/**
 * The form's values as an edit. A blank optional field is sent as `null`, which rgip
 * reads as "clear it"; every field is sent, so nothing the person emptied comes back.
 */
export function asEdits(values: MemoryFormValues): MemoryEdits {
  return {
    kind: values.kind,
    text: values.text.trim(),
    instead_of: values.instead_of.trim() || null,
    reason: values.reason.trim() || null,
  };
}

/** The form's values as a new entry. A blank optional field is left out. */
export function asNewMemory(
  values: MemoryFormValues,
  modelId: string | null
): NewMemory {
  const insteadOf = values.instead_of.trim();
  const reason = values.reason.trim();
  return {
    key:
      values.scope === 'model' && modelId ? modelKey(modelId) : ALL_MODELS_KEY,
    kind: values.kind,
    text: values.text.trim(),
    ...(insteadOf ? { instead_of: insteadOf } : {}),
    ...(reason ? { reason } : {}),
  };
}

/**
 * "Override for this model": a model entry on THIS model that replaces `inherited`, an
 * approved entry from a model it was copied from. rgip hides the inherited one for this
 * copy and leaves it on its source.
 */
export function asOverride(
  values: MemoryFormValues,
  inherited: Pick<MemoryEntry, 'entry_id'>,
  modelId: string
): NewMemory {
  return {
    ...asNewMemory({ ...values, scope: 'model' }, modelId),
    supersedes_id: inherited.entry_id,
  };
}

/** `instead of X · why: Y`, leaving out whichever part is missing. */
export function entryDetail(entry: {
  instead_of?: string | null;
  reason?: string | null;
}): string {
  const parts: string[] = [];
  if (entry.instead_of?.trim()) parts.push(`instead of ${entry.instead_of}`);
  if (entry.reason?.trim()) parts.push(`why: ${entry.reason}`);
  return parts.join(' · ');
}

/**
 * The exact moment behind "2 hours ago" or a decision's date, for a hover title. Undefined
 * when the entry carries no readable date, so the title is left off rather than reading
 * "Invalid Date".
 */
export function exactTime(
  entry: MemoryEntry,
  proposed: boolean
): string | undefined {
  const at = Date.parse(
    (proposed ? entry.created_at : entry.decided_at ?? entry.created_at) ?? ''
  );
  return Number.isNaN(at) ? undefined : new Date(at).toLocaleString();
}

/** Who or what proposed it, and when, in a few words. */
export function proposedBy(
  entry: MemoryEntry,
  now: number = Date.now()
): string {
  const who = entry.proposed_by_email ? ` · ${entry.proposed_by_email}` : '';
  // The same wording the session lists use (`whenReadable`), so "2 hours ago" means one
  // thing across the panel. A reviewer needs it to tell this morning's proposal from a
  // stale one nobody remembers making.
  const age = whenReadable(entry.created_at, now);
  const when = age ? ` · ${age}` : '';
  if (entry.origin === 'chat') return `Suggested in chat${who}${when}`;
  if (entry.origin === 'reviewer')
    return `Suggested from a past session${who}${when}`;
  return `Added${who}${when}`;
}

/**
 * Who saved a decision and when, in a few words: "Approved by" for one the assistant
 * suggested and a person accepted, "Added by" for one a person wrote themselves. The
 * date is absolute: a decision is a record, and "3 days ago" stops saying when.
 */
export function decidedBy(entry: MemoryEntry): string {
  const verb =
    entry.origin === 'chat' || entry.origin === 'reviewer'
      ? 'Approved'
      : 'Added';
  const who = entry.decided_by_email ? ` by ${entry.decided_by_email}` : '';
  const at = Date.parse(entry.decided_at ?? entry.created_at ?? '');
  const when = Number.isNaN(at)
    ? ''
    : ` · ${new Date(at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}`;
  return `${verb}${who}${when}`;
}

export function kindLabel(kind: string): string {
  return MEMORY_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

export interface MemoryGroups {
  proposed: MemoryEntry[];
  tenant: MemoryEntry[];
  /** This model's own approved entries, on a model's view. */
  model: MemoryEntry[];
  /** Approved entries inherited from the models this one was copied from. */
  inherited: MemoryEntry[];
  /** Every other model's approved entries, by model id, on the tenant view. */
  byModel: { modelId: string; entries: MemoryEntry[] }[];
}

/**
 * The list split the way the Memory view draws it. `modelId` is the model on screen, or
 * null on the models page, where every model's entries are grouped under their model.
 */
export function groupMemory(
  entries: MemoryEntry[],
  modelId: string | null
): MemoryGroups {
  const groups: MemoryGroups = {
    proposed: [],
    tenant: [],
    model: [],
    inherited: [],
    byModel: [],
  };
  const byModel = new Map<string, MemoryEntry[]>();
  entries.forEach((entry) => {
    if (entry.status === 'proposed') groups.proposed.push(entry);
    else if (entry.status !== 'approved') return;
    else if (scopeOf(entry) === 'tenant') groups.tenant.push(entry);
    else if (entry.inherited_from_model_id) groups.inherited.push(entry);
    else if (modelId !== null && entry.model_id === modelId)
      groups.model.push(entry);
    else {
      const key = entry.model_id ?? '';
      byModel.set(key, [...(byModel.get(key) ?? []), entry]);
    }
  });
  groups.byModel = Array.from(byModel.entries())
    .sort(([a], [b]) => Number(a) - Number(b) || a.localeCompare(b))
    .map(([id, rows]) => ({ modelId: id, entries: rows }));
  return groups;
}
